import dayjs from 'dayjs';
import { useRef } from 'react';
import { SmoothLineChart } from '../../../components/Charts';
import { useState, useEffect } from 'react';
const StationChart = ({
  powerMeterDemand,
  powerMeterDemandLoading,
  powerMeterDemandError,
  cblData,
  cblLoading,
  cblError,
  refetchCbl,
  predictionCblData,
  predictionCblLoading,
  predictionCblError,
  refetchPredictionCbl,
  powerMeterDemandYesterday,
  // 실시간 업데이트 정보
  nextUpdateTime,
  isUpdating,
  manualUpdate,
  drHistoryLatestData,
  powerMeterDemandDrEvent
}) => {
  const today = dayjs().format('YYYY-MM-DD');
  const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
  const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
  const [cssColors, setCssColors] = useState({
    graphSetZ1: '#8C62FF',
    graphSetZ2: '#FB85A1',
    graphSetZ3: '#38BDF8',
    graphSetZ4: '#FEC02E',
    gray600: '#718096',
  });
  useEffect(() => {
    const getCSSVariable = (variable) => {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(variable)
        .trim();
    };
    setCssColors({
      graphSetZ1: getCSSVariable('--graph-set-z1'),
      graphSetZ2: getCSSVariable('--graph-set-z2'),
      graphSetZ3: getCSSVariable('--graph-set-z3'),
      graphSetZ4: getCSSVariable('--graph-set-z4'),
      gray600: getCSSVariable('--gray-600'),
    });
  }, []);
  // 차트 ref들
  const powerMeterChartRef = useRef(null);
  const cblChartRef = useRef(null);
  const drHistoryChartRef = useRef(null);

  // 마지막 업데이트 시간 계산 (15분 단위로 반올림, 10초 지연)
  const getLastUpdateTime = () => {
    const now = dayjs();
    const currentMinute = now.minute();
    const currentSecond = now.second();
    
    // 현재 분을 15분 단위로 내림
    const lastUpdateMinute = Math.floor(currentMinute / 15) * 15;
    
    return now.minute(lastUpdateMinute).second(10).millisecond(0);
  };
  
  const calculatedLastUpdateTime = getLastUpdateTime();
  const transformPowerMeterData = (powerMeterData, isRealtime = false) => {

    if (!powerMeterData || typeof powerMeterData !== 'object') {
      return { labels: [], data: [] };
    }

    const labels = [];
    const data = [];
    
    // 실시간 데이터인 경우 마지막 업데이트 시간 계산
    let lastUpdateIndex = 95; // 기본적으로 전체 24시간 (P00 ~ P95)
    
    if (isRealtime) {
      // 마지막 업데이트 시간까지의 인덱스 계산
      const lastUpdateTime = getLastUpdateTime();
      const lastUpdateHour = lastUpdateTime.hour();
      const lastUpdateMinute = lastUpdateTime.minute();
      lastUpdateIndex = lastUpdateHour * 4 + (lastUpdateMinute / 15);
    }

    // 항상 전체 24시간 (P00 ~ P95) 생성
    for (let i = 0; i <= 95; i++) {
      const hour = Math.floor(i / 4); // 4개씩 그룹화 (15분 * 4 = 1시간)
      const minute = (i % 4) * 15; // 0, 15, 30, 45분
      
      // 시간 형식: HH:MM
      const timeLabel = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      
      // P00, P01, P02... 형태의 키 생성
      const key = `P${i.toString().padStart(2, '0')}`;
      
      labels.push(timeLabel);
      
      // 실시간 데이터인 경우 마지막 업데이트 시간 이후는 null
      if (isRealtime && i > lastUpdateIndex) {
        data.push(null);
      } else {
        data.push(powerMeterData[key] || 0);
      }
    }
    return { labels: labels, data: data };
  };

  const getDRDispatchInfo = () => {
    if (!drHistoryLatestData?.timeSlots || !Array.isArray(drHistoryLatestData.timeSlots)) {
      return null;
    }

    // timeSlots를 숫자로 변환하고 정렬
    const slotNumbers = drHistoryLatestData.timeSlots
      .map(timeSlot => parseInt(timeSlot.replace('P', '')))
      .filter(slot => slot >= 0 && slot <= 95)
      .sort((a, b) => a - b);

    if (slotNumbers.length === 0) {
      return null;
    }

    return {
      startIndex: slotNumbers[0],
      endIndex: slotNumbers[slotNumbers.length - 1],
      timeSlots: drHistoryLatestData.timeSlots
    };
  };

  const drDispatchInfo = getDRDispatchInfo();

  // 전력 수요 데이터가 변경될 때 차트 부드럽게 업데이트
  useEffect(() => {
    if (powerMeterDemand && powerMeterChartRef.current) {
      const realtimeData = transformPowerMeterData(powerMeterDemand, true);
      const yesterdayData = transformPowerMeterData(powerMeterDemandYesterday?.TimeSlotSumValue, false);
      
      const newData = {
        labels: realtimeData.labels, // 24시간 전체 labels 사용
        datasets: [
          {
            label: '실시간 전력 수요 (kWh)',
            data: realtimeData.data,
            borderColor: cssColors.graphSetZ3,
            backgroundColor: cssColors.graphSetZ3,
            fill: false,
            tension: 0.1,
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 3,
          },
          {
            label: '어제 전력 수요 (kWh)',
            data: yesterdayData.data,
            borderColor: cssColors.graphSetZ4,
            backgroundColor: cssColors.graphSetZ4,
            fill: false,
            tension: 0.1,
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 3,
          },
          
        ]
      };
      powerMeterChartRef.current.updateChart(newData);
    }
  }, [powerMeterDemand, powerMeterDemandYesterday]);

  // CBL 데이터가 변경될 때 차트 부드럽게 업데이트
  useEffect(() => {
    if (powerMeterDemand && cblChartRef.current) {
      const realtimePowerData = transformPowerMeterData(powerMeterDemand, true);
      const predictionCblDataTransformed = transformPowerMeterData(predictionCblData?.TimeSlotSumValue, false)
      const realtimeCblData = transformPowerMeterData(cblData?.TimeSlotSumValue, false);
      
      const newData = {
        labels: realtimePowerData.labels, // 24시간 전체 labels 사용
        datasets: [
          {
            label: '실시간 전력 수요 (kWh)',
            data: realtimePowerData.data,
            borderColor: cssColors.graphSetZ3,
            backgroundColor: cssColors.graphSetZ3,
            fill: false,
            tension: 0.1,
            borderWidth: 2
          },
          {
            label: 'CBL',
            data: predictionCblDataTransformed.data,
            borderColor: cssColors.graphSetZ1,
            backgroundColor: cssColors.graphSetZ1,
            fill: false,
            tension: 0.1,
            borderDash: [5, 5],  // 점선 패턴 [선 길이, 간격 길이]
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 3,
          },
          {
            label: '실시간 CBL',
            data: realtimeCblData.data,
            borderColor: cssColors.graphSetZ2,
            backgroundColor: cssColors.graphSetZ2,
            fill: false,
            tension: 0.1,
            borderDash: [5, 5],  // 점선 패턴 [선 길이, 간격 길이]
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 3,
          }
        ]
      };
      cblChartRef.current.updateChart(newData);
    }
  }, [powerMeterDemand, predictionCblData, cblData]);

  // DR 발령 데이터가 변경될 때 차트 부드럽게 업데이트
  useEffect(() => {
    if (drHistoryLatestData && drHistoryChartRef.current) {
      const newData = {
        labels: transformPowerMeterData(powerMeterDemand, true).labels,
        datasets: [
          {
            label: '실시간 전력 수요 (kWh)',
            data: transformPowerMeterData(powerMeterDemand, true).data,
            borderColor: cssColors.graphSetZ3,
            backgroundColor: cssColors.graphSetZ3,
            fill: false,
            tension: 0.1,
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 3,
          },
          {
            label: 'DR 발령일 전력 수요 (kWh)',
            data: transformPowerMeterData(powerMeterDemandDrEvent?.TimeSlotSumValue, false).data,
            borderColor: cssColors.graphSetZ4,
            backgroundColor: cssColors.graphSetZ4,
            fill: false,
            tension: 0.1,
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 3,
          }
        ]
      }
      drHistoryChartRef.current.updateChart(newData);
    }
  }, [drHistoryLatestData]);
  return (
    <>
      <div className='flex flex-col gap-10 mb-20'>
        <h3 className='text-b1-700 text-base'>충전소 실시간 - 어제 전력 수요 비교</h3>
        <p className='text-b2-700 text-gray-600'>집계 기준 : {yesterday} - {today}</p>
        <div style={{ height: "440px" }}>
          <SmoothLineChart
            ref={powerMeterChartRef}
            data={{
              labels: transformPowerMeterData(powerMeterDemand, true).labels,
              datasets: [
                {
                  label: '실시간 전력 수요 (kWh)',
                  data: transformPowerMeterData(powerMeterDemand, true).data,
                  borderColor: cssColors.graphSetZ3,
                  backgroundColor: cssColors.graphSetZ3,
                  fill: false,
                  tension: 0.1,  // 부드러운 곡선 제거, 직선으로 연결
                  borderWidth: 2,
                  pointRadius: 2,
                  pointHoverRadius: 3,
                },
                {
                  label: '어제 전력 수요 (kWh)',
                  data: transformPowerMeterData(powerMeterDemandYesterday?.TimeSlotSumValue, false).data,
                  borderColor: cssColors.graphSetZ4,
                  backgroundColor: cssColors.graphSetZ4,
                  fill: false,
                  tension: 0.1,
                  borderWidth: 2,
                  pointRadius: 2,
                  pointHoverRadius: 3,
                }
              ]
            }}
            title="24시간 전력 수요 추이 (15분 간격)"
            height="420px"
            lastUpdateTime={calculatedLastUpdateTime}
            yMin={0}
          />
        </div>
      </div>
      
      <div className='flex flex-col gap-10 mb-20'>
        <h3 className='text-b1-700 text-base'>충전소 실시간 - CBL 비교</h3>
        <p className='text-b2-700 text-gray-600'>마지막 집계 시각 : {calculatedLastUpdateTime.format('YYYY-MM-DD HH:mm')}</p>
        <div style={{ height: "440px" }}>
          <SmoothLineChart
            ref={cblChartRef}
            data={{
              labels: transformPowerMeterData(powerMeterDemand, true).labels,
              datasets: [
                {
                  label: '실시간 전력 수요 (kWh)',
                  data: transformPowerMeterData(powerMeterDemand, true).data,
                  borderColor: cssColors.graphSetZ3,
                  backgroundColor: cssColors.graphSetZ3,
                  fill: false,
                  tension: 0.1,  // 부드러운 곡선 제거, 직선으로 연결
                  borderWidth: 2,
                  pointRadius: 2,
                  pointHoverRadius: 3,
                },
                {
                  label: 'CBL',
                  data: transformPowerMeterData(predictionCblData?.TimeSlotSumValue, false).data,
                  borderColor: cssColors.graphSetZ1,
                  backgroundColor: cssColors.graphSetZ1,
                  fill: false,
                  tension: 0.1,  // 부드러운 곡선 제거, 직선으로 연결
                  borderDash: [5, 5],  // 점선 패턴 [선 길이, 간격 길이]
                  borderWidth: 2,
                  pointRadius: 2,
                  pointHoverRadius: 3,
                },
                {
                  label: '실시간 CBL',
                  data: transformPowerMeterData(cblData?.TimeSlotSumValue, false).data,
                  borderColor: cssColors.graphSetZ2,
                  backgroundColor: cssColors.graphSetZ2,
                  fill: false,
                  tension: 0.1,  // 부드러운 곡선 제거, 직선으로 연결
                  borderDash: [5, 5],  // 점선 패턴 [선 길이, 간격 길이]
                  borderWidth: 2,
                  pointRadius: 2,
                  pointHoverRadius: 3,
                }
              ]
            }}
            title="24시간 전력 수요 추이 (15분 간격)"
            height="420px"
            lastUpdateTime={calculatedLastUpdateTime}
            yMin={0}
          />
        </div>
      </div>
      
      <div className='flex flex-col gap-10 mb-20'>
        <h3 className='text-b1-700 text-base'>충전소 실시간 - 최근 DR발령일 비교</h3>
        <p className='text-b2-700 text-gray-600'>마지막 DR 발령일 : {drHistoryLatestData?.date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')} {drHistoryLatestData?.startTime} - {drHistoryLatestData?.endTime}</p>
        <div style={{ height: "440px" }}>
          <SmoothLineChart
            ref={drHistoryChartRef}
            data={{
              labels: transformPowerMeterData(powerMeterDemand, true).labels,
              datasets: [
                {
                  label: '실시간 전력 수요 (kWh)',
                  data: transformPowerMeterData(powerMeterDemand, true).data,
                  borderColor: cssColors.graphSetZ3,
                  backgroundColor: cssColors.graphSetZ3,
                  fill: false,
                  tension: 0.1,  // 부드러운 곡선 제거, 직선으로 연결,
                  borderWidth: 2,  // 라인 굵기 조정
                  pointRadius: 2,
                  pointHoverRadius: 3,
                },
                {
                  label: 'DR 발령일 전력 수요 (kWh)',
                  data: transformPowerMeterData(powerMeterDemandDrEvent?.TimeSlotSumValue, false).data,
                  borderColor: cssColors.graphSetZ4,
                  backgroundColor: cssColors.graphSetZ4,
                  fill: false,
                  tension: 0.1,  // 부드러운 곡선 제거, 직선으로 연결
                  borderWidth: 2,  // 라인 굵기 조정
                  pointRadius: 2,
                  pointHoverRadius: 3,
                },
              ]
            }}
            title="24시간 전력 수요 추이 (15분 간격)"
            height="420px"
            lastUpdateTime={calculatedLastUpdateTime}
            drDispatchInfo={drDispatchInfo}
            isToday={true}
            yMin={0}
          />
        </div>
      </div>
      
    </>
  )
}

export default StationChart